import React, { useCallback, useEffect, useState } from 'react'
import { useGlobalTemplatePresetInputParameters } from '../../hooks'
import { TemplateInputParameters } from '../../../../../../models/AiFeature'
import { PresetTemplateConfigForm } from '../PresetTemplateConfigForm'
import { AiFeaturePreset } from '../../../../../../models/AiFeaturePreset'
import { YesNoCancelModal } from '../../../../../../components/Modal/YesNoCancelModal'
import { Button } from '../../../../../../components/Button/Button'
import { PresetConfigLoader } from '../../Common/PresetConfigLoader'
import { useInputParametersUpdateScope } from '../../../../../../components/InputParameterHierarchy/hooks'
import { MeetingTemplateInputParametersHierarchyConfigComponentProps } from '../../../../../../components/InputParameterHierarchy/model'
import { useAppSelector } from '../../../../../../store/hooks'
import { selectAccountId } from '../../../../../../store/auth/authSlice'
import { useGetPresetByIdQuery } from '../../../../../../store/ai/ai'

export const GlobalPresetTemplateConfigForm: React.FC<MeetingTemplateInputParametersHierarchyConfigComponentProps> = ({ typeId, onHide }) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: presetData } = useGetPresetByIdQuery({ accountId, presetId: typeId })

  const { presetInputParameters, presetDefaultInputParameters, updateInputParameters, updateLoading } = useGlobalTemplatePresetInputParameters(presetData?.data)

  if (!presetData || !presetData.data || !presetInputParameters || !presetDefaultInputParameters) {
    return <PresetConfigLoader />
  }

  return (
    <GlobalTemplateConfigFormBody
      preset={presetData.data}
      inputParameters={presetInputParameters}
      defaultInputParameters={presetDefaultInputParameters}
      onSave={updateInputParameters}
      onHide={onHide}
      updateLoading={updateLoading}
    />
  )
}

interface GlobalTemplateConfigFormBodyProps {
  preset: AiFeaturePreset
  inputParameters: TemplateInputParameters
  defaultInputParameters: TemplateInputParameters
  onSave: (inputParameters: TemplateInputParameters) => Promise<void>
  onHide: () => void
  updateLoading: boolean
}

const GlobalTemplateConfigFormBody: React.FC<GlobalTemplateConfigFormBodyProps> = ({
  preset,
  inputParameters,
  defaultInputParameters,
  onSave,
  onHide,
  updateLoading,
}) => {
  const { hasAiFeatureUpdateScope } = useInputParametersUpdateScope()
  const disabled = !hasAiFeatureUpdateScope

  const [editedInputParameters, setEditedInputParameters] = useState<TemplateInputParameters>(inputParameters)
  const [isChanged, setIsChanged] = useState<boolean>(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [shouldReset, setShouldReset] = useState<boolean>(false)
  const [showGenerate, setShowGenerate] = useState<boolean>(false)
  const [isGenerating, setGenerating] = useState<boolean>(false)

  const handleClickBack = () => {
    if (showGenerate) {
      setShowGenerate(false)
    } else if (isChanged && !disabled) {
      setShowConfirmation(true)
    } else {
      onHide()
    }
  }

  const handleClickCancel = () => {
    setShouldReset(true)
    setIsChanged(false)
  }

  const handleClickSave = async () => {
    setIsChanged(false)
    await onSave(editedInputParameters)
  }

  useEffect(() => {
    const { example_1 = '', example_2 = '', template = '' } = inputParameters
    const { example_1: displayedExample_1 = '', example_2: displayedExample_2 = '', template: displayedTemplate = '' } = editedInputParameters
    setIsChanged(
      displayedTemplate.trim() !== template.trim() || displayedExample_1.trim() !== example_1.trim() || displayedExample_2.trim() !== example_2.trim(),
    )
    setShouldReset(false)
  }, [editedInputParameters, inputParameters])

  const handleChangeTemplate = useCallback((newTemplate: string) => {
    setEditedInputParameters((inputParameters) => ({ ...inputParameters, template: newTemplate.trim() }))
  }, [])

  const handleChangeExample = useCallback((field: string, newExample: string) => {
    setEditedInputParameters((inputParameters) => ({ ...inputParameters, [field]: newExample.trim() }))
  }, [])

  return (
    <>
      <YesNoCancelModal
        show={showConfirmation}
        onHide={() => setShowConfirmation(false)}
        message={'Do you want to save your changes?'}
        onYes={() => {
          onSave(editedInputParameters)
          onHide()
        }}
        onNo={() => {
          setEditedInputParameters(inputParameters)
          onHide()
        }}
      />
      <PresetTemplateConfigForm
        preset={preset}
        defaultInputParameters={defaultInputParameters}
        inputParameters={inputParameters}
        parentInputParameters={inputParameters}
        editedInputParameters={editedInputParameters}
        showCustomizationSetting={false}
        customized={true}
        shouldReset={shouldReset}
        shouldShowGenerate={showGenerate}
        showGenerate={() => setShowGenerate(true)}
        onChangeTemplate={handleChangeTemplate}
        onChangeExample={handleChangeExample}
        onGenerate={setGenerating}
        disabled={disabled}
      />
      <div className='d-flex justify-content-between w-100 mt-3'>
        <Button variant='borderless' onClick={handleClickBack}>
          {showGenerate ? 'Back' : 'Close'}
        </Button>
        {hasAiFeatureUpdateScope && (
          <div className='d-flex gap-2'>
            <Button variant='secondary' onClick={handleClickCancel} disabled={!isChanged} loading={updateLoading || isGenerating}>
              Cancel
            </Button>
            <Button variant='primary' onClick={handleClickSave} disabled={!isChanged} loading={updateLoading || isGenerating}>
              Save
            </Button>
          </div>
        )}
      </div>
    </>
  )
}
